import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Target, RotateCcw, Check, X, ArrowRight, Users, ShieldAlert, DollarSign, Search, Building2 } from "lucide-react";

const API = '/api';

export default function ScoreEntry() {
  const { matchId: urlMatchId } = useParams();
  const { isAdmin, getAuthHeader } = useAuth();
  const [matches, setMatches] = useState([]);
  const [shooters, setShooters] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(urlMatchId || "");
  const [selectedShooter, setSelectedShooter] = useState("");
  const [shooterIdSearch, setShooterIdSearch] = useState("");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [teamName, setTeamName] = useState("");
  const [scores, setScores] = useState([]);
  const [currentTarget, setCurrentTarget] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastScore, setLastScore] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [isReEntry, setIsReEntry] = useState(false);
  const [markAsPaid, setMarkAsPaid] = useState(false);
  const [paidBySponsor, setPaidBySponsor] = useState(false);
  const [currentShooterSponsor, setCurrentShooterSponsor] = useState(null);

  const shotsPerRound = matchData?.shots_per_round || 25;
  const isTeamEvent = matchData?.is_team_event || matchData?.is_protection || false;
  const entryFee = parseFloat(matchData?.entry_fee) || 0;

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedMatch) {
      fetchEntries();
      fetchMatchData();
    }
  }, [selectedMatch]);

  const fetchInitialData = async () => {
    try {
      const [matchesRes, shootersRes, sponsorsRes] = await Promise.all([
        axios.get(`${API}/matches?status=active`),
        axios.get(`${API}/shooters`),
        axios.get(`${API}/sponsors`),
      ]);
      setMatches(matchesRes.data || []);
      setShooters(shootersRes.data || []);
      setSponsors(sponsorsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchData = async () => {
    try {
      const res = await axios.get(`${API}/matches/${selectedMatch}`);
      setMatchData(res.data);
      setScores(Array(res.data.shots_per_round || 25).fill(null));
      setCurrentTarget(0);
    } catch (error) {
      console.error("Failed to fetch match data");
    }
  };

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/entries?match_id=${selectedMatch}`);
      setEntries(res.data);
    } catch (error) {
      toast.error("Failed to fetch entries");
    }
  };

  // Find shooter by shooter_id
  const handleShooterIdSearch = () => {
    if (!shooterIdSearch.trim()) {
      toast.error("Please enter a shooter ID");
      return;
    }
    const found = shooters.find(
      s => s.shooter_id?.toLowerCase() === shooterIdSearch.toLowerCase().trim()
    );
    if (found) {
      setSelectedShooter(found.id);
      handleShooterSelect(found.id);
      toast.success(`Found: ${found.name}`);
    } else {
      toast.error("Shooter ID not found");
    }
  };

  const handleShooterSelect = async (shooterId) => {
    try {
      setSelectedShooter(shooterId);
      setShowSuccess(false);
      setPaidBySponsor(false);
      setMarkAsPaid(false);
      
      // Get shooter's sponsor info
      const shooter = shooters.find(s => s.id === shooterId);
      if (shooter && shooter.sponsor_id) {
        const sponsor = sponsors.find(sp => sp.id === shooter.sponsor_id);
        setCurrentShooterSponsor(sponsor || { id: shooter.sponsor_id, name: 'Sponsor' });
      } else {
        setCurrentShooterSponsor(null);
      }
      
      if (isTeamEvent) {
        setSelectedEntry(null);
        resetScores();
        return;
      }

      if (!selectedMatch) {
        toast.error("Please select a match first");
        return;
      }

      // Always fetch fresh entries to avoid race conditions
      let currentEntries = [];
      try {
        const entriesRes = await axios.get(`${API}/entries?match_id=${selectedMatch}`);
        currentEntries = Array.isArray(entriesRes.data) ? entriesRes.data : [];
        setEntries(currentEntries);
      } catch (e) {
        console.error("Failed to fetch entries:", e);
        currentEntries = [];
      }

      // Check if entry already exists for this shooter in this match
      let entry = currentEntries.find(
        (e) => e.shooter_id === shooterId && e.match_id === selectedMatch
      );

      if (entry) {
        // Existing entry - this is a re-entry
        setIsReEntry(true);
        setSelectedEntry(entry);
        resetScores();
      } else {
        // New entry - create it
        setIsReEntry(false);
        try {
          const res = await axios.post(`${API}/entries`, {
            match_id: selectedMatch,
            shooter_id: shooterId,
          }, { headers: getAuthHeader() });
          entry = res.data;
          if (entry && entry.id) {
            setEntries(prev => [...prev, entry]);
            setSelectedEntry(entry);
            toast.success("Shooter registered for match");
            resetScores();
          } else {
            throw new Error("Invalid entry response");
          }
        } catch (error) {
          const errorMsg = error.response?.data?.detail || error.response?.data?.error || error.message || "Failed to register shooter";
          // If shooter already registered, find and use existing entry
          if (errorMsg.toLowerCase().includes("already registered") || errorMsg.toLowerCase().includes("already exists")) {
            // Refresh entries and try to find it
            try {
              const entriesRes = await axios.get(`${API}/entries?match_id=${selectedMatch}`);
              const updatedEntries = Array.isArray(entriesRes.data) ? entriesRes.data : [];
              setEntries(updatedEntries);
              entry = updatedEntries.find(e => e.shooter_id === shooterId);
              if (entry) {
                setIsReEntry(true);
                setSelectedEntry(entry);
                resetScores();
                toast.info("Shooter already registered - ready for re-entry");
                return;
              }
            } catch (e) {
              console.error("Failed to refresh entries:", e);
            }
          }
          toast.error(errorMsg);
          setSelectedEntry(null);
          return;
        }
      }
    } catch (err) {
      console.error("Error in handleShooterSelect:", err);
      toast.error("An error occurred. Please try again.");
    }
  };

  const handleTeamEntry = async () => {
    if (!selectedShooter || !teamName.trim()) {
      toast.error("Please select a shooter and enter a team name");
      return;
    }

    try {
      const res = await axios.post(`${API}/entries`, {
        match_id: selectedMatch,
        shooter_id: selectedShooter,
        team_name: teamName.trim(),
      }, { headers: getAuthHeader() });
      setSelectedEntry(res.data);
      setEntries([...entries, res.data]);
      toast.success("Shooter registered for team");
      resetScores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to register shooter");
    }
  };

  const resetScores = useCallback(() => {
    setScores(Array(shotsPerRound).fill(null));
    setCurrentTarget(0);
    setMarkAsPaid(false);
    setPaidBySponsor(false);
  }, [shotsPerRound]);

  const handleScore = (hit) => {
    if (currentTarget >= shotsPerRound) return;

    const newScores = [...scores];
    newScores[currentTarget] = hit;
    setScores(newScores);
    setCurrentTarget(currentTarget + 1);
  };

  const handleSubmit = async () => {
    if (!selectedEntry) {
      toast.error("Please select a shooter first");
      return;
    }

    const scoredCount = scores.filter((s) => s !== null).length;
    if (scoredCount !== shotsPerRound) {
      toast.error(`Please score all ${shotsPerRound} targets (${scoredCount}/${shotsPerRound} completed)`);
      return;
    }

    setSubmitting(true);
    try {
      const roundData = {
        entry_id: selectedEntry.id,
        scores: scores,
        mark_as_paid: markAsPaid || paidBySponsor,
        paid_by_sponsor: paidBySponsor,
      };
      
      // Include sponsor_id if paid by sponsor
      if (paidBySponsor && currentShooterSponsor) {
        roundData.sponsor_id = currentShooterSponsor.id;
      }
      
      const res = await axios.post(`${API}/rounds`, roundData, { headers: getAuthHeader() });

      setLastScore({
        total: res.data.total_hits || res.data.score,
        streak: res.data.longest_streak || res.data.longest_run,
        shooter: selectedEntry.shooter_name,
        team: selectedEntry.team_name,
        paid: markAsPaid,
        sponsorPaid: paidBySponsor,
        sponsorName: paidBySponsor ? currentShooterSponsor?.name : null,
      });
      setShowSuccess(true);
      toast.success(`Round submitted! Score: ${res.data.total_hits || res.data.score}/${shotsPerRound}, Longest Run: ${res.data.longest_streak || res.data.longest_run}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || "Failed to submit round");
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickReEntry = () => {
    setShowSuccess(false);
    setIsReEntry(true);
    resetScores();
  };

  const handleNewShooter = () => {
    setShowSuccess(false);
    setSelectedShooter("");
    setSelectedEntry(null);
    setTeamName("");
    setShooterIdSearch("");
    setIsReEntry(false);
    setCurrentShooterSponsor(null);
    resetScores();
  };

  const totalHits = scores.filter((s) => s === true).length;
  const totalMisses = scores.filter((s) => s === false).length;
  const scoredCount = scores.filter((s) => s !== null).length;

  const calculateLongestRun = (scoreArray) => {
    let maxRun = 0;
    let currentRun = 0;
    for (const score of scoreArray) {
      if (score === true) {
        currentRun++;
        maxRun = Math.max(maxRun, currentRun);
      } else if (score === false) {
        currentRun = 0;
      }
    }
    return maxRun;
  };

  const getShooterById = (id) => shooters.find(s => s.id === id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="score-entry-loading">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!isAdmin()) {
    return (
      <div data-testid="score-entry-page" className="space-y-6">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Score Entry
          </h1>
          <p className="text-slate-500 mt-1">Record round scores</p>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <ShieldAlert className="mx-auto text-amber-500 mb-4" size={48} />
            <h3 className="font-semibold text-lg mb-2">Admin Access Required</h3>
            <p className="text-slate-500">
              Only administrators can record scores. Contact an admin to enter your scores.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showSuccess && lastScore) {
    return (
      <div data-testid="score-success-page" className="space-y-6">
        <Card className="bg-green-50 border-green-200">
          <CardContent className="py-12 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <Check size={40} className="text-green-700" />
            </div>
            <h2 className="font-serif text-3xl font-bold text-green-800 mb-2">
              Round Submitted!
            </h2>
            <p className="text-green-700 text-lg mb-1">
              {lastScore.shooter} {lastScore.team && `(${lastScore.team})`}
            </p>
            {lastScore.sponsorPaid && (
              <p className="text-purple-600 text-sm flex items-center justify-center gap-1">
                <Building2 size={14} /> Paid by Sponsor: {lastScore.sponsorName}
              </p>
            )}
            {lastScore.paid && !lastScore.sponsorPaid && (
              <p className="text-green-600 text-sm flex items-center justify-center gap-1">
                <DollarSign size={14} /> Marked as Paid
              </p>
            )}
            <div className="flex justify-center gap-8 mt-6">
              <div>
                <p className="text-sm text-green-600 uppercase">Score</p>
                <p className="font-mono text-4xl font-bold text-green-800">
                  {lastScore.total}/{shotsPerRound}
                </p>
              </div>
              <div>
                <p className="text-sm text-green-600 uppercase">Longest Run</p>
                <p className="font-mono text-4xl font-bold text-orange-600">
                  {lastScore.streak}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={handleQuickReEntry}
            className="h-16 px-8 text-lg bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
            data-testid="quick-reentry-btn"
          >
            <ArrowRight size={24} className="mr-2" />
            Re-Entry (Same Shooter)
          </Button>
          <Button
            onClick={handleNewShooter}
            variant="outline"
            className="h-16 px-8 text-lg"
            data-testid="new-shooter-btn"
          >
            <Users size={24} className="mr-2" />
            New Shooter
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="score-entry-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
          Score Entry
        </h1>
        <p className="text-slate-500 mt-1">Record round scores ({shotsPerRound} targets)</p>
      </div>

      {/* Selection */}
      <Card>
        <CardContent className="p-6">
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="block text-sm font-medium text-slate-700 mb-2">
                Select Match
              </Label>
              <Select
                value={selectedMatch}
                onValueChange={(val) => {
                  setSelectedMatch(val);
                  setSelectedShooter("");
                  setSelectedEntry(null);
                  setTeamName("");
                  setShooterIdSearch("");
                  setIsReEntry(false);
                }}
              >
                <SelectTrigger data-testid="match-select">
                  <SelectValue placeholder="Choose a match" />
                </SelectTrigger>
                <SelectContent>
                  {matches.map((match) => (
                    <SelectItem key={match.id} value={match.id}>
                      {match.name} ({match.shots_per_round || 25} shots) - ${match.entry_fee || 0}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Shooter ID Search */}
            <div>
              <Label className="block text-sm font-medium text-slate-700 mb-2">
                Find Shooter by ID
              </Label>
              <div className="flex gap-2">
                <Input
                  value={shooterIdSearch}
                  onChange={(e) => setShooterIdSearch(e.target.value)}
                  placeholder="Enter Shooter ID"
                  onKeyDown={(e) => e.key === 'Enter' && handleShooterIdSearch()}
                  disabled={!selectedMatch}
                  data-testid="shooter-id-search"
                />
                <Button 
                  onClick={handleShooterIdSearch} 
                  disabled={!selectedMatch}
                  variant="outline"
                >
                  <Search size={18} />
                </Button>
              </div>
            </div>
          </div>

          {/* Team Name Input for Team Events */}
          {isTeamEvent && selectedShooter && !selectedEntry && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-3">
                <Users size={20} className="text-[#1B3B6F]" />
                <Label className="font-medium">Team Event - Enter Team Name</Label>
              </div>
              <div className="flex gap-3">
                <Input
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Enter team name"
                  data-testid="team-name-input"
                  className="flex-1"
                />
                <Button
                  onClick={handleTeamEntry}
                  className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                  data-testid="register-team-btn"
                >
                  Register
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Score Entry Grid */}
      {selectedEntry && (
        <>
          {/* Entry Fee Notice */}
          {isReEntry && entryFee > 0 && (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <DollarSign className="text-amber-600" size={24} />
                    <div>
                      <p className="font-medium text-amber-800">Re-Entry Fee: ${entryFee.toFixed(2)}</p>
                      <p className="text-sm text-amber-600">This is an additional round for {selectedEntry.shooter_name}</p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {/* Sponsor Paid - only shows if shooter has sponsor */}
                    {currentShooterSponsor && (
                      <label className="flex items-center gap-2 cursor-pointer bg-purple-100 px-3 py-2 rounded-lg">
                        <input
                          type="checkbox"
                          checked={paidBySponsor}
                          onChange={(e) => {
                            setPaidBySponsor(e.target.checked);
                            if (e.target.checked) setMarkAsPaid(false);
                          }}
                          disabled={markAsPaid}
                          className="w-5 h-5 rounded border-purple-400 text-purple-600 focus:ring-purple-500"
                          data-testid="sponsor-paid-checkbox"
                        />
                        <Building2 size={16} className="text-purple-600" />
                        <span className="text-purple-800 font-medium">Sponsor: {currentShooterSponsor.name}</span>
                      </label>
                    )}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={markAsPaid}
                        onChange={(e) => {
                          setMarkAsPaid(e.target.checked);
                          if (e.target.checked) setPaidBySponsor(false);
                        }}
                        disabled={paidBySponsor}
                        className="w-5 h-5 rounded border-amber-400 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-amber-800 font-medium">Mark as Paid</span>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* First Entry Fee Notice */}
          {!isReEntry && entryFee > 0 && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <DollarSign className="text-blue-600" size={24} />
                    <div>
                      <p className="font-medium text-blue-800">Entry Fee: ${entryFee.toFixed(2)}</p>
                      <p className="text-sm text-blue-600">First round for {selectedEntry.shooter_name}</p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {/* Sponsor Paid - only shows if shooter has sponsor */}
                    {currentShooterSponsor && (
                      <label className="flex items-center gap-2 cursor-pointer bg-purple-100 px-3 py-2 rounded-lg">
                        <input
                          type="checkbox"
                          checked={paidBySponsor}
                          onChange={(e) => {
                            setPaidBySponsor(e.target.checked);
                            if (e.target.checked) setMarkAsPaid(false);
                          }}
                          disabled={markAsPaid}
                          className="w-5 h-5 rounded border-purple-400 text-purple-600 focus:ring-purple-500"
                          data-testid="sponsor-paid-checkbox"
                        />
                        <Building2 size={16} className="text-purple-600" />
                        <span className="text-purple-800 font-medium">Sponsor: {currentShooterSponsor.name}</span>
                      </label>
                    )}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={markAsPaid}
                        onChange={(e) => {
                          setMarkAsPaid(e.target.checked);
                          if (e.target.checked) setPaidBySponsor(false);
                        }}
                        disabled={paidBySponsor}
                        className="w-5 h-5 rounded border-blue-400 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-blue-800 font-medium">Mark as Paid</span>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Live Stats */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-green-700 uppercase tracking-wider">Hits</p>
                <p className="font-mono text-4xl font-bold text-green-700" data-testid="hits-count">
                  {totalHits}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-orange-700 uppercase tracking-wider">Misses</p>
                <p className="font-mono text-4xl font-bold text-orange-600" data-testid="misses-count">
                  {totalMisses}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-slate-50">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-slate-600 uppercase tracking-wider">Progress</p>
                <p className="font-mono text-4xl font-bold text-slate-700" data-testid="progress-count">
                  {scoredCount}/{shotsPerRound}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Target Grid */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <Target className="text-[#1B3B6F]" />
                Shot Recording
                {selectedEntry.team_name && (
                  <span className="text-sm font-normal text-slate-500 ml-2">
                    Team: {selectedEntry.team_name}
                  </span>
                )}
              </CardTitle>
              <p className="text-sm text-slate-500">Click each shot to toggle Hit/Miss. Green = Hit, Red = Miss.</p>
            </CardHeader>
            <CardContent className="p-4 lg:p-6">
              {/* Group shots in rows of 5 */}
              <div className="space-y-4 max-w-4xl mx-auto">
                {Array.from({ length: Math.ceil(shotsPerRound / 5) }, (_, rowIndex) => {
                  const startShot = rowIndex * 5;
                  const endShot = Math.min(startShot + 5, shotsPerRound);
                  const rowScores = scores.slice(startShot, endShot);
                  const rowHits = rowScores.filter(s => s === true).length;
                  
                  return (
                    <div key={rowIndex} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                      <div className="w-16 text-xs font-medium text-slate-500 text-center">
                        {startShot + 1}-{endShot}
                      </div>
                      
                      <div className="flex gap-2 flex-1">
                        {rowScores.map((score, localIndex) => {
                          const globalIndex = startShot + localIndex;
                          return (
                            <button
                              key={globalIndex}
                              onClick={() => {
                                const newScores = [...scores];
                                if (newScores[globalIndex] === null) {
                                  newScores[globalIndex] = true;
                                } else if (newScores[globalIndex] === true) {
                                  newScores[globalIndex] = false;
                                } else {
                                  newScores[globalIndex] = true;
                                }
                                setScores(newScores);
                                if (score === null && currentTarget === globalIndex) {
                                  setCurrentTarget(globalIndex + 1);
                                }
                              }}
                              className={`
                                w-12 h-12 sm:w-14 sm:h-14 rounded-lg border-2 flex flex-col items-center justify-center
                                transition-all duration-150 font-mono text-sm font-bold
                                ${score === null 
                                  ? "border-slate-300 bg-white hover:border-slate-400 text-slate-400" 
                                  : score === true 
                                    ? "border-green-500 bg-green-100 text-green-700 hover:bg-green-200" 
                                    : "border-red-500 bg-red-100 text-red-700 hover:bg-red-200"
                                }
                                ${currentTarget === globalIndex && score === null ? "ring-2 ring-[#1B3B6F] ring-offset-1" : ""}
                              `}
                              data-testid={`shot-${globalIndex}`}
                            >
                              <span className="text-[10px] opacity-60">{globalIndex + 1}</span>
                              {score === null && <span className="text-lg">-</span>}
                              {score === true && <Check size={20} className="text-green-600" />}
                              {score === false && <X size={20} className="text-red-600" />}
                            </button>
                          );
                        })}
                      </div>
                      
                      <div className="w-16 text-center">
                        <span className={`font-mono font-bold ${rowHits === 5 ? "text-green-600" : rowHits === 0 && rowScores.every(s => s !== null) ? "text-red-600" : "text-slate-600"}`}>
                          {rowScores.filter(s => s !== null).length > 0 
                            ? `${rowHits}/${rowScores.filter(s => s !== null).length}` 
                            : "-"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Longest Run Display */}
              {scoredCount > 0 && (
                <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200 max-w-md mx-auto">
                  <div className="text-center">
                    <p className="text-sm text-amber-700 uppercase tracking-wider mb-1">Current Longest Run</p>
                    <p className="font-mono text-3xl font-bold text-amber-800">
                      {calculateLongestRun(scores)}
                    </p>
                    <p className="text-xs text-amber-600 mt-1">Consecutive hits for tiebreaker</p>
                  </div>
                </div>
              )}

              {/* Score Buttons */}
              {currentTarget < shotsPerRound && (
                <div className="flex gap-4 justify-center mt-8">
                  <Button
                    onClick={() => handleScore(true)}
                    className="h-20 w-32 text-xl font-bold bg-green-700 hover:bg-green-600"
                    data-testid="hit-btn"
                  >
                    <Check size={32} className="mr-2" />
                    HIT
                  </Button>
                  <Button
                    onClick={() => handleScore(false)}
                    className="h-20 w-32 text-xl font-bold bg-orange-500 hover:bg-orange-400"
                    data-testid="miss-btn"
                  >
                    <X size={32} className="mr-2" />
                    MISS
                  </Button>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4 justify-center mt-6">
                <Button
                  variant="outline"
                  onClick={resetScores}
                  data-testid="reset-btn"
                >
                  <RotateCcw size={18} className="mr-2" />
                  Reset
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={scoredCount !== shotsPerRound || submitting}
                  className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90 px-8"
                  data-testid="submit-round-btn"
                >
                  {submitting ? "Submitting..." : "Submit Round"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* No Selection Message */}
      {!selectedEntry && selectedMatch && !isTeamEvent && (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">Enter a shooter ID or select from the list to begin scoring</p>
          </CardContent>
        </Card>
      )}

      {!selectedMatch && (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">Select a match to get started</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
